import {atom} from 'jotai';
import type {ApolloError} from '@apollo/client';
import type {RunTicket} from '@/types/well';
import {isAwaitingReview} from '@/lib/runTicketMapping';

/**
 * Lifted out of RunTicketsPage's own local state so it's a single
 * global source of truth — the nav badge (BottomNav/TopNav) and the
 * background reconciliation hook (useRunTicketReconciliation) both need
 * to read/react to the same ticket list RunTicketsPage's table shows,
 * not a copy scoped to whichever page happens to be mounted.
 *
 * REMOVED 2026-08-17: used to be seeded with SAMPLE_RUN_TICKETS (3 fake
 * tickets, see the now-orphaned sampleRunTickets.ts) as its initial
 * value, and useRunTicketReconciliation's sync effect only ever
 * overwrote that seed when RunTicket_AllDocument resolved with a
 * truthy result — so on any settled error (offline before the cache
 * ever populated, or a genuine server error) the 3 fake tickets stayed
 * forever, including in the nav badge/needsReview count below. Starts
 * empty now; useRunTicketReconciliation keeps this in sync with real
 * data unconditionally (see its ticketsLoading-gated effect).
 */
export const runTicketsAtom = atom<RunTicket[]>([]);

/**
 * Published by useRunTicketReconciliation from its own RunTicket_All
 * query so pages that read runTicketsAtom without running that query
 * themselves (RunTicketsPage, AuditReportPage) can still show an
 * accurate loading/error state via DataStatusBanner instead of just
 * trusting whatever's currently in runTicketsAtom.
 */
export const runTicketsLoadingAtom = atom<boolean>(true);
export const runTicketsErrorAtom = atom<ApolloError | null>(null);

/**
 * How many tickets are currently awaiting review — drives the nav badge
 * and RunTicketsPage's own chip off the same number. Uses
 * isAwaitingReview (runTicketMapping.ts) rather than a raw
 * `scanStatus === 'NeedsReview'` check — corrected 2026-08-25 per Duane,
 * who confirmed real tickets needing review actually carry scanStatus
 * 'Scanned'.
 */
export const needsReviewCountAtom = atom(
   (get) => get(runTicketsAtom).filter((t) => isAwaitingReview(t.scanStatus)).length,
);

/**
 * Set by a notification's "Review" action (see useRunTicketReconciliation)
 * just before navigating to Run Tickets, so the page can land already
 * filtered instead of making the user click the chip themselves too.
 * RunTicketsPage reads this once on mount and clears it right after.
 */
export const pendingRunTicketFocusAtom = atom<'needsReview' | 'unmatched' | null>(null);

/**
 * Added 2026-08-24 per Duane: RunTicketsPage's "Needs Review"/"Unmatched"
 * toolbar chips used to be local useState, so navigating away (e.g. to
 * paste a ticket number into a well's Daily Report) and back reset the
 * filter to "show everything" — annoying mid-review workflow when you're
 * working through a queue of Unmatched tickets one at a time. Lifted here
 * so the selection survives page navigation; only reset intentionally
 * (see pendingRunTicketFocusAtom usage in RunTicketsPage, which now writes
 * here too instead of a separate local setState).
 */
export const runTicketQuickFiltersAtom = atom<Array<'needsReview' | 'unmatched'>>([]);

/** A Daily Report that names a run ticket number with no matching RunTicket at all — the "other direction" mismatch from a ticket's own Unmatched status. */
export interface UnmatchedReport {
   reportId: string;
   wellId: string;
   wellName: string;
   entryDate: string;
   runTicketNumber: string;
}

/**
 * Computed by useRunTicketReconciliation (polls DailyWellReport data,
 * cross-references against runTicketsAtom) and read by RunTicketsPage's
 * "reports missing a ticket" alert — there's no dedicated Daily Reports
 * list page for this to live on instead, so it surfaces here alongside
 * the ticket-side Unmatched status rather than on a page that doesn't
 * exist yet.
 */
export const unmatchedReportsAtom = atom<UnmatchedReport[]>([]);

/**
 * RunTicket ids with no Daily Report referencing their ticketNumber —
 * computed by useRunTicketReconciliation. RunTicketsPage overrides a
 * ticket's displayed status to 'Unmatched' when its id is in this set,
 * rather than trusting whatever runTicketMapping.ts's toRunTicket
 * placeholder set on the ticket itself.
 */
export const unmatchedTicketIdsAtom = atom<Set<string>>(new Set<string>());

// REMOVED 2026-08-21 per Duane: a real Balanced/Variance quantity check
// (TicketReconciliation/ticketReconciliationAtom, SRS 3.8, added just the
// day before) used to live here — diffing a matched ticket's nsvBarrels
// against its Daily Report's oilBarrelRuns and flagging anything outside
// VARIANCE_TOLERANCE_BBLS. The client decided there's no good way to
// calculate a useful variance: pumpers don't record how much a tank was
// drawn down for shipping, tanks often take pumping from multiple wells,
// and a tank isn't always emptied completely — so a computed mismatch
// couldn't mean anything reliable. The run ticket itself is the final
// authority; the pumper's own review of it is the only check now. See
// useRunTicketReconciliation.ts's matching removal.
